import React from 'react';
import { EditBase, ListBase, useEditContext } from 'react-admin';
import { Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import JsonPanel from '../Common/JsonPanel';
import MainCard from '../Common/MainCard';
import RoutableTabs from '../Common/RoutableTabs';
import { RoutableTabConfig } from '@src/types';
import { getTagForChangeState } from '../Common/Tags';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../helpers/utils';
import { spinnerElement } from '../Common/utils';
import ChangeInstancesListPage from '../ChangeInstances/List/ChangeInstancesListPage';
import ConsumerChangeDetail from '../ChangeInstances/List/ConsumerChangeDetail';
import { ChangeInstancesListConfig } from '../ChangeInstances/List/types';
import MetadataDiffView from './SubComponents/MetadataDiffView';
import './Submissions.css';

/**
 * The changes this submission raised, in the same list the change instances
 * page uses — same columns, same headers, same endless scroll. The submission
 * is a permanent filter rather than one of the reader's, so it is not offered
 * as a control and cannot be cleared.
 */
const changesConfig: ChangeInstancesListConfig = {
    resource: 'change_instances',
    serviceItemPath: '/service_items',
    applicationsResource: 'applications',
    serviceItemsResource: 'service_items',
    servicesResource: 'consumer_services',
    partyField: {
        label: 'Service Owner',
        team: (record) => record.service_owner_team ?? record.owner ?? record.service?.owner,
        pov: 'serviceowner',
    },
    ExpandedRow: ConsumerChangeDetail,
};

const SubmissionChanges = ({ submissionId }: { submissionId: number }) => (
    <ListBase
        resource="change_instances"
        basePath="/change_instances"
        sort={{ field: 'id', order: 'DESC' }}
        filter={{ light: true, submission_id: submissionId }}
        perPage={25}
    >
        <ChangeInstancesListPage config={changesConfig} />
    </ListBase>
);

const SubmissionsDetailView = () => {
    const { record, loading } = useEditContext<any>();

    if (loading || !record) return spinnerElement;

    const tabs: RoutableTabConfig[] = [
        /* The declaration leads: it is what was submitted, and the changes
           below are what came of it. Opening on the changes asked the reader
           to work out what had been asked for from what happened. */
        {
            key: 'declaration',
            tab: 'Declaration',
            content: (
                // fills the card rather than stopping short of it: a declaration
                // is long, and the room below it would be room to read in
                <div className="sub-tab sub-tab--fill">
                    {/* the page keeps its own class on the frame, so its rules still apply */}
                    <JsonPanel data={record.request_json ?? {}} className="sub-json" enableYaml format="yaml" />
                </div>
            ),
        },
        {
            key: 'change_instances',
            tab: 'Change Instances',
            // the tab is the table's, as on the integration pages' Responses
            // tab: it fills the card, its header held under the tabs and only
            // its rows scrolling (nx-fill, NetOrcaTableListV1.css)
            content: (
                <div className="sub-tab--fill sub-tab--table nx-fill">
                    <SubmissionChanges submissionId={record.id} />
                </div>
            ),
        },
        {
            key: 'metadata',
            tab: 'Metadata',
            content: (
                <div className="sub-tab">
                    <MetadataDiffView submission={record} />
                </div>
            ),
        },
    ];

    return (
        <div className="netorca-sub-detail netorca-frame">
            {/* What the submission is, and what came of it. Which submission
                it is comes from the breadcrumb above — the header does not say
                it a second time. */}
            <header className="sub-header">
                <div className="sub-header__main">
                    <Tooltip title="Back to submissions">
                        <Link className="sub-back" to="/submissions" aria-label="Back to submissions">
                            <ArrowLeftOutlined aria-hidden="true" />
                        </Link>
                    </Tooltip>

                    <div className="sub-facts">
                        <span className="sub-fact">
                            <span className="sub-fact__label">Commit</span>
                            <Tooltip title={record.commit_id || undefined}>
                                <span className={`sub-fact__value${record.commit_id ? ' sub-fact__value--mono' : ''}`}>
                                    {record.commit_id ? record.commit_id.slice(0, 8) : '—'}
                                </span>
                            </Tooltip>
                        </span>

                        <span className="sub-fact">
                            <span className="sub-fact__label">Changes</span>
                            <span className="sub-fact__value">{record.num_change_instances ?? 0}</span>
                        </span>

                        {record.submitted_by?.username && (
                            <span className="sub-fact">
                                <span className="sub-fact__label">Submitted by</span>
                                <span className="sub-fact__value">{record.submitted_by.username}</span>
                            </span>
                        )}
                    </div>

                    <div className="sub-header__states">
                        {getTagForChangeState(record.change_state)}
                        {record.created && (
                            <Tooltip title={formatDateAndTime(record.created)}>
                                <span className="sub-created">
                                    Created {formatDateAndTimeFromNow(record.created)}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </header>

            <MainCard className="sub-panel">
                <RoutableTabs className="sub-tabs" tabs={tabs} defaultActiveKey="declaration" />
            </MainCard>
        </div>
    );
};

const SubmissionsDetail = (props: any) => (
    <EditBase {...props}>
        <SubmissionsDetailView />
    </EditBase>
);

export default SubmissionsDetail;
